import LoadingButton from '@mui/lab/LoadingButton';
import {
  FormControl,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import usePortfolioForm from '../../../hooks/usePortfolioForm';
import SelectButton from '../../buttons/SelectButton';
import DateRangePickerButton from '../../buttons/DateRangePickerButton';
import getPortfolioConfig from '../../../constants/portfolio-type-config';

const PortfolioTab = ({ product, customCompany, client = true }) => {
  const {
    formik,
    portfolioFormats,
    portfolioTypesAvailable,
    loadingPortfolio,
    handleRangeChange,
  } = usePortfolioForm(product, customCompany, client);

  const portfolioConfig = useMemo(
    () => getPortfolioConfig(formik.values.portfolioType),
    [formik.values.portfolioType],
  );

  const availableFormats = useMemo(() => {
    if (portfolioFormats && portfolioConfig.availableFormats) {
      return portfolioFormats.filter(
        (format) => portfolioConfig.availableFormats.includes(format.value),
      );
    }
    return [];
  }, [portfolioFormats, portfolioConfig.availableFormats]);

  const portfolioTypeButtonRef = useRef(null);
  const documentFormatButtonRef = useRef(null);
  const dates = {
    startDate: formik.values.startDate,
    endDate: formik.values.endDate,
  };

  const handleDatesRangeChange = useCallback((newDates) => {
    handleRangeChange({
      range: {
        startDate: newDates.dateIssued_Gte,
        endDate: newDates.dateIssued_Lte,
      },
    });
  }, [handleRangeChange]);

  const SelectOptions = (types) => types.map(({ value, label }) => (
    <MenuItem value={value} key={value}>
      {label}
    </MenuItem>
  ));

  const errorDisplay = (name) => formik.touched[name] && formik.errors[name] && (
  <div style={{ color: 'red', marginTop: '0.5rem' }}>
    {formik.errors[name]}
  </div>
  );

  const SelectButtonValue = (types, emptyLabel) => (selected) => {
    if (!selected) return emptyLabel;
    if (!types.some((opt) => opt.value === selected)) return '';
    return types.find((opt) => opt.value === selected)?.label || selected;
  };

  return (
    <Formik
      initialValues={formik.initialValues}
      validationSchema={formik.validationSchema}
      onSubmit={formik.handleSubmit}
    >
      <Form id="portfolioDownload" noValidate>
        <Stack width="100%" spacing={2}>
          <Typography variant="h1" color="text.primary" mb={2}>Selecciona y descarga</Typography>
          <Stack width="100%" spacing={2} alignItems="flex-start" direction="row" justifyContent="space-between">
            <FormControl
              required
              fullWidth
              error={
                  formik.touched.portfolioType
                  && !!formik.errors.portfolioType
                }
            >
              <SelectButton
                id="portfolio-type"
                name="portfolioType"
                value={formik.values.portfolioType}
                buttonRef={portfolioTypeButtonRef}
                onChange={formik.handleChange}
                displayEmpty
                renderValue={SelectButtonValue(portfolioTypesAvailable, 'Tipo de documento')}
              >
                {SelectOptions(portfolioTypesAvailable)}
              </SelectButton>
              {errorDisplay('portfolioType')}
            </FormControl>
            <FormControl
              required
              fullWidth
              error={
                formik.touched.documentFormat
                && !!formik.errors.documentFormat
              }
            >
              <SelectButton
                id="document-format"
                name="documentFormat"
                value={formik.values.documentFormat}
                onChange={formik.handleChange}
                buttonRef={documentFormatButtonRef}
                displayEmpty
                renderValue={SelectButtonValue(availableFormats, 'Formato')}
              >
                {SelectOptions(availableFormats)}
              </SelectButton>
              {errorDisplay('documentFormat')}
            </FormControl>
            <FormControl
              required
              fullWidth
              error={formik.touched.startDate && !!formik.errors.startDate}
            >
              <DateRangePickerButton
                dates={dates}
                setDates={handleDatesRangeChange}
                disabled={!portfolioConfig.useDatePicker}
              />
            </FormControl>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={loadingPortfolio}
              id="download-portfolio"
              sx={{ width: '60%' }}
              startIcon={<FileDownloadOutlinedIcon />}
            >
              Descargar resumen
            </LoadingButton>
          </Stack>
        </Stack>
      </Form>
    </Formik>
  );
};

PortfolioTab.propTypes = {
  product: PropTypes.string.isRequired,
  customCompany: PropTypes.number,
  client: PropTypes.bool,
};

PortfolioTab.defaultProps = {
  customCompany: null,
  client: true,
};

export default PortfolioTab;
